import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Ban,
  CheckCircle2,
  Copy,
  History,
  KeyRound,
  Loader2,
  PlugZap,
  Pencil,
  Send,
  Trash2,
  Webhook,
  X,
} from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "../../lib/api";
import { FEATURE_KEYS, normalizeRole } from "../../lib/features";
import ApiDocumentationPanel from "./ApiDocumentationPanel";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const TABS = [
  { id: "home", label: "Início" },
  { id: "api-keys", label: "Chaves de API" },
  { id: "webhooks", label: "Webhooks" },
  { id: "documentation", label: "Documentação da API" },
];

const DEFAULT_EVENTS = [
  "lead.created",
  "lead.updated",
  "lead.moved",
  "deal.won",
  "deal.lost",
  "task.created",
  "task.completed",
  "message.received",
  "message.sent",
  "contact.created",
];

const STATUS_LABELS = {
  enabled: "Habilitada",
  disabled: "Desabilitada",
  expired: "Expirada",
  deleted: "Removida",
  active: "Ativo",
  inactive: "Inativo",
};

function formatDate(value) {
  if (!value) return "Sem expiração";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("pt-BR");
}

function StatusBadge({ status }) {
  const active = ["enabled", "active"].includes(status);
  const expired = status === "expired";
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
      active
        ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-400"
        : expired
          ? "border-amber-400/20 bg-amber-400/10 text-amber-400"
          : "border-slate-700 bg-slate-800/70 text-slate-500"
    }`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function ModalShell({ title, onClose, children, maxWidth = "max-w-lg" }) {
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" className="absolute inset-0 bg-black/75 backdrop-blur-md" onClick={onClose} aria-label="Fechar modal" />
      <div className={`relative w-full ${maxWidth} overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-900 shadow-2xl`}>
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <h3 className="text-sm font-bold text-slate-100" style={{ fontFamily: "Outfit" }}>{title}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-800 hover:text-slate-200">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EmptyRow({ colSpan, icon: Icon, text }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-5 py-10 text-center">
        <Icon className="mx-auto h-5 w-5 text-slate-700" />
        <p className="mt-2 text-xs text-slate-500">{text}</p>
      </td>
    </tr>
  );
}

function LoadingRow({ colSpan }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-5 py-10 text-center">
        <Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" />
        <p className="mt-2 text-xs text-slate-500">Carregando...</p>
      </td>
    </tr>
  );
}

function ApiKeysPanel({ items, loading, showRemoved, onToggleRemoved, onCreate, onDisable, onDelete }) {
  const safeItems = Array.isArray(items) ? items : [];
  return (
    <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10">
            <KeyRound className="h-5 w-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-100" style={{ fontFamily: "Outfit" }}>Chaves de API</h3>
            <p className="mt-2 max-w-2xl text-xs leading-relaxed text-slate-500">
              As chaves de API permitem que sistemas externos acessem o Cliver CRM com segurança. Trate sua chave como uma senha e compartilhe apenas com sistemas confiáveis.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          <Button type="button" variant="outline" onClick={onToggleRemoved} className="h-9 border-slate-700 bg-transparent px-3 text-xs text-slate-400 hover:bg-slate-800 hover:text-slate-200">
            <History className="mr-1.5 h-3.5 w-3.5" />
            {showRemoved ? "Ocultar removidas" : "Histórico de removidas"}
          </Button>
          <Button type="button" onClick={onCreate} className="h-9 bg-cyan-400 px-4 text-xs font-semibold text-slate-950 hover:bg-cyan-300">
            Gerar chave de API
          </Button>
        </div>
      </div>

      <div className="mt-5 overflow-x-auto rounded-xl border border-slate-800 bg-slate-950/35">
        <table className="w-full min-w-[720px] text-left">
          <thead className="border-b border-slate-800 bg-slate-950/60">
            <tr>
              {['Nome', 'Chave', 'Data de expiração', 'Situação', 'Último uso', 'Ações'].map((column) => (
                <th key={column} className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-600">{column}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/70">
            {loading ? <LoadingRow colSpan={6} /> : safeItems.length === 0 ? (
              <EmptyRow colSpan={6} icon={KeyRound} text="Nenhuma chave de API criada ainda." />
            ) : safeItems.map((item) => (
              <tr key={item.id} className="text-xs text-slate-400">
                <td className="px-4 py-3 font-medium text-slate-200">{item.name}</td>
                <td className="px-4 py-3 font-mono text-slate-500">•••• {item.last_four}</td>
                <td className="px-4 py-3">{formatDate(item.expires_at)}</td>
                <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                <td className="px-4 py-3">{item.last_used_at ? formatDate(item.last_used_at) : "Nunca"}</td>
                <td className="px-4 py-3">
                  {item.status !== "deleted" && (
                    <div className="flex items-center gap-1">
                      {item.status === "enabled" && (
                        <button type="button" title="Desabilitar" aria-label={`Desabilitar chave ${item.name}`} onClick={() => onDisable(item)} className="rounded-lg p-2 text-slate-500 hover:bg-amber-400/10 hover:text-amber-400">
                          <Ban className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button type="button" title="Remover" aria-label={`Remover chave ${item.name}`} onClick={() => onDelete(item)} className="rounded-lg p-2 text-slate-500 hover:bg-red-400/10 hover:text-red-400">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

function WebhooksPanel({ items, loading, onCreate, onEdit, onTest, onLogs, onDisable, onDelete }) {
  const safeItems = Array.isArray(items) ? items : [];
  return (
    <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-400/10">
            <Webhook className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-100" style={{ fontFamily: "Outfit" }}>Meus Webhooks</h3>
            <p className="mt-2 max-w-2xl text-xs leading-relaxed text-slate-500">Configure URLs externas para receber eventos do Cliver CRM em tempo real.</p>
          </div>
        </div>
        <Button type="button" onClick={onCreate} className="h-9 shrink-0 bg-emerald-400 px-4 text-xs font-semibold text-slate-950 hover:bg-emerald-300">
          Adicionar Webhook
        </Button>
      </div>

      <div className="mt-5 overflow-x-auto rounded-xl border border-slate-800 bg-slate-950/35">
        <table className="w-full min-w-[760px] text-left">
          <thead className="border-b border-slate-800 bg-slate-950/60">
            <tr>
              {['Nome', 'URL', 'Situação', 'Eventos', 'Ações'].map((column) => (
                <th key={column} className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-600">{column}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/70">
            {loading ? <LoadingRow colSpan={5} /> : safeItems.length === 0 ? (
              <EmptyRow colSpan={5} icon={Webhook} text="Nenhum webhook criado ainda." />
            ) : safeItems.map((item) => (
              <tr key={item.id} className="text-xs text-slate-400">
                <td className="px-4 py-3 font-medium text-slate-200">{item.name}</td>
                <td className="max-w-[240px] truncate px-4 py-3 font-mono text-[11px] text-slate-500" title={item.url}>{item.url}</td>
                <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                <td className="px-4 py-3"><span className="text-slate-300">{Array.isArray(item.events) ? item.events.length : 0}</span> evento(s)</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button type="button" title="Editar webhook" aria-label={`Editar webhook ${item.name}`} onClick={() => onEdit(item)} className="rounded-lg p-2 text-slate-500 hover:bg-emerald-400/10 hover:text-emerald-400">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" title="Testar webhook" aria-label={`Testar webhook ${item.name}`} onClick={() => onTest(item)} className="rounded-lg p-2 text-slate-500 hover:bg-cyan-400/10 hover:text-cyan-400">
                      <Send className="h-3.5 w-3.5" />
                    </button>
                    {item.status === "active" && (
                      <button type="button" title="Desabilitar" aria-label={`Desabilitar webhook ${item.name}`} onClick={() => onDisable(item)} className="rounded-lg p-2 text-slate-500 hover:bg-amber-400/10 hover:text-amber-400">
                        <Ban className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button type="button" title="Ver logs" aria-label={`Ver logs do webhook ${item.name}`} onClick={() => onLogs(item)} className="rounded-lg p-2 text-slate-500 hover:bg-violet-400/10 hover:text-violet-400">
                      <History className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" title="Remover" aria-label={`Remover webhook ${item.name}`} onClick={() => onDelete(item)} className="rounded-lg p-2 text-slate-500 hover:bg-red-400/10 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

function WebhookLogsModal({ webhook, logs, loading, canViewTechnical, selectedLog, onSelectLog, onClose }) {
  const formatLogDate = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("pt-BR");
  };

  return (
    <ModalShell title={`Logs - ${webhook?.name || "Webhook"}`} onClose={onClose} maxWidth="max-w-6xl">
      <div className="max-h-[75vh] overflow-auto p-5">
        <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-950/35">
          <table className="w-full min-w-[1050px] text-left">
            <thead className="border-b border-slate-800 bg-slate-950/60">
              <tr>
                {["Evento", "Status", "Data/Hora", "URL de destino", "Codigo HTTP", "Tempo", "Tentativas", "Erro", "Ações"].map((column) => (
                  <th key={column} className="px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-600">{column}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {loading ? <LoadingRow colSpan={9} /> : logs.length === 0 ? (
                <EmptyRow colSpan={9} icon={History} text="Nenhuma entrega registrada para este webhook." />
              ) : logs.map((log) => {
                const success = log.status === "success";
                const expanded = selectedLog?.id === log.id;
                return (
                  <React.Fragment key={log.id}>
                    <tr className="text-xs text-slate-400">
                      <td className="px-3 py-3 font-mono text-slate-300">{log.event || "-"}</td>
                      <td className="px-3 py-3"><span className={success ? "text-emerald-400" : "text-red-400"}>{success ? "Sucesso" : "Falha"}</span></td>
                      <td className="whitespace-nowrap px-3 py-3">{formatLogDate(log.created_at)}</td>
                      <td className="max-w-[220px] truncate px-3 py-3 font-mono text-[11px]" title={log.url || ""}>{log.url || "-"}</td>
                      <td className="px-3 py-3">{log.status_code ?? "-"}</td>
                      <td className="px-3 py-3">{log.response_time_ms == null ? "-" : `${log.response_time_ms} ms`}</td>
                      <td className="px-3 py-3">{log.attempts ?? 1}</td>
                      <td className="max-w-[220px] truncate px-3 py-3 text-red-300" title={log.error_message || ""}>{log.error_message || "-"}</td>
                      <td className="px-3 py-3">
                        {canViewTechnical && (
                          <Button type="button" variant="outline" onClick={() => onSelectLog(expanded ? null : log)} className="h-8 border-slate-700 bg-transparent px-3 text-[11px] text-slate-400 hover:bg-slate-800">
                            Ver detalhes tecnicos
                          </Button>
                        )}
                      </td>
                    </tr>
                    {expanded && canViewTechnical && (
                      <tr>
                        <td colSpan={9} className="bg-slate-950/70 px-4 py-3">
                          <div className="grid gap-2 text-[11px] text-slate-500 sm:grid-cols-2">
                            <p><span className="text-slate-300">Log ID:</span> {log.id || "-"}</p>
                            <p><span className="text-slate-300">Webhook ID:</span> {log.webhook_id || "-"}</p>
                            <p><span className="text-slate-300">Evento:</span> {log.event || "-"}</p>
                            <p><span className="text-slate-300">Resultado:</span> {log.status || "-"}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </ModalShell>
  );
}

export default function ExternalIntegrationsPanel({ canManage = true, userRole = "", features = {} }) {
  const [activeTab, setActiveTab] = useState("home");
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState(DEFAULT_EVENTS);
  const [loading, setLoading] = useState(true);
  const [showRemoved, setShowRemoved] = useState(false);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createdSecret, setCreatedSecret] = useState(null);
  const [apiForm, setApiForm] = useState({ name: "", expires_at: "" });
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [webhookForm, setWebhookForm] = useState({ name: "", url: "", secret: "", events: [] });
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const normalizedRole = normalizeRole(userRole);
  const canViewTechnical = ["owner", "admin", "super_admin"].includes(normalizedRole);

  const featureOn = (feature) => normalizedRole === "super_admin" || Boolean(features?.[feature]);
  const hasExternalShell = featureOn(FEATURE_KEYS.EXTERNAL_INTEGRATIONS);
  const canUseApiKeys = featureOn(FEATURE_KEYS.API_KEYS);
  const canUseWebhooks = featureOn(FEATURE_KEYS.WEBHOOKS);
  const canUseDocs = featureOn(FEATURE_KEYS.API_DOCS);
  const visibleTabs = useMemo(
    () => TABS.filter((tab) => {
      if (tab.id === "home") return canUseApiKeys || canUseWebhooks || (hasExternalShell && !canUseDocs);
      if (tab.id === "api-keys") return canUseApiKeys;
      if (tab.id === "webhooks") return canUseWebhooks;
      if (tab.id === "documentation") return canUseDocs;
      return false;
    }),
    [hasExternalShell, canUseApiKeys, canUseWebhooks, canUseDocs]
  );

  const loadData = useCallback(async () => {
    if (!canManage) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [keysResult, webhooksResult] = await Promise.allSettled([
        canUseApiKeys ? api.get("/integrations/api-keys", { params: { include_deleted: showRemoved } }) : Promise.resolve({ data: { items: [] } }),
        canUseWebhooks ? api.get("/integrations/webhooks") : Promise.resolve({ data: { items: [], events: DEFAULT_EVENTS } }),
      ]);

      if (keysResult.status === "fulfilled") {
        setApiKeys(Array.isArray(keysResult.value.data?.items) ? keysResult.value.data.items : []);
      } else if (canUseApiKeys) {
        setApiKeys([]);
        toast.error(formatApiError(keysResult.reason?.response?.data?.detail || "Não foi possível carregar as chaves de API."));
      }

      if (webhooksResult.status === "fulfilled") {
        setWebhooks(Array.isArray(webhooksResult.value.data?.items) ? webhooksResult.value.data.items : []);
        setEvents(Array.isArray(webhooksResult.value.data?.events) ? webhooksResult.value.data.events : DEFAULT_EVENTS);
      } else if (canUseWebhooks) {
        setWebhooks([]);
        setEvents(DEFAULT_EVENTS);
        toast.error(formatApiError(webhooksResult.reason?.response?.data?.detail || "Não foi possível carregar os webhooks."));
      }
    } finally {
      setLoading(false);
    }
  }, [canManage, showRemoved, canUseApiKeys, canUseWebhooks]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!visibleTabs.length) return;
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [activeTab, visibleTabs]);

  const visiblePanels = useMemo(() => activeTab === "home" ? [canUseApiKeys && "api-keys", canUseWebhooks && "webhooks"].filter(Boolean) : [activeTab], [activeTab, canUseApiKeys, canUseWebhooks]);
  const showExternalEmptyState = activeTab === "home" && visiblePanels.length === 0;

  const closeModal = () => {
    if (saving) return;
    setEditingWebhook(null);
    setModal(null);
    if (createdSecret) setCreatedSecret(null);
  };

  const createApiKey = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const expiresAt = apiForm.expires_at ? new Date(`${apiForm.expires_at}T23:59:59`).toISOString() : null;
      const { data } = await api.post("/integrations/api-keys", { name: apiForm.name, expires_at: expiresAt });
      setCreatedSecret(data.api_key);
      setApiForm({ name: "", expires_at: "" });
      setModal("api-secret");
      await loadData();
      toast.success("Chave de API criada.");
    } catch (error) {
      toast.error(formatApiError(error?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openWebhookModal = (item = null) => {
    setEditingWebhook(item);
    setWebhookForm(item ? { name: item.name || "", url: item.url || "", secret: "", events: Array.isArray(item.events) ? [...item.events] : [] } : { name: "", url: "", secret: "", events: [] });
    setModal("webhook-create");
  };

  const createWebhook = async (event) => {
    event.preventDefault();
    if (!webhookForm.events.length) {
      toast.error("Selecione pelo menos um evento.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...webhookForm };
      if (editingWebhook && !payload.secret) delete payload.secret;
      if (editingWebhook) await api.put(`/integrations/webhooks/${editingWebhook.id}`, payload);
      else await api.post("/integrations/webhooks", payload);
      setWebhookForm({ name: "", url: "", secret: "", events: [] });
      setModal(null);
      setEditingWebhook(null);
      await loadData();
      toast.success(editingWebhook ? "Webhook atualizado." : "Webhook criado.");
    } catch (error) {
      toast.error(formatApiError(error?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const mutate = async (request, successMessage) => {
    try {
      await request();
      await loadData();
      toast.success(successMessage);
    } catch (error) {
      toast.error(formatApiError(error?.response?.data?.detail));
    }
  };

  const disableKey = (item) => {
    if (window.confirm(`Desabilitar a chave "${item.name}"?`)) {
      mutate(() => api.patch(`/integrations/api-keys/${item.id}/disable`), "Chave desabilitada.");
    }
  };
  const deleteKey = (item) => {
    if (window.confirm(`Remover a chave "${item.name}"? Esta ação não pode ser desfeita.`)) {
      mutate(() => api.delete(`/integrations/api-keys/${item.id}`), "Chave removida.");
    }
  };
  const disableWebhook = (item) => {
    if (window.confirm(`Desabilitar o webhook "${item.name}"?`)) {
      mutate(() => api.patch(`/integrations/webhooks/${item.id}/disable`), "Webhook desabilitado.");
    }
  };
  const deleteWebhook = (item) => {
    if (window.confirm(`Remover o webhook "${item.name}"? Esta ação não pode ser desfeita.`)) {
      mutate(() => api.delete(`/integrations/webhooks/${item.id}`), "Webhook removido.");
    }
  };
  const testWebhook = async (item) => {
    try {
      const { data } = await api.post(`/integrations/webhooks/${item.id}/test`);
      if (data.success) toast.success("Webhook respondeu com sucesso.");
      else toast.error(data.delivery?.error_message || "O endpoint não respondeu com sucesso.");
    } catch (error) {
      toast.error(formatApiError(error?.response?.data?.detail));
    }
  };
  const openWebhookLogs = async (item) => {
    setSelectedWebhook(item);
    setWebhookLogs([]);
    setSelectedLog(null);
    setModal("webhook-logs");
    setLogsLoading(true);
    try {
      const { data } = await api.get(`/integrations/webhooks/${item.id}/logs`);
      setWebhookLogs(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      toast.error(formatApiError(error?.response?.data?.detail || "Não foi possível carregar os logs."));
    } finally {
      setLogsLoading(false);
    }
  };


  const toggleEvent = (eventName) => {
    setWebhookForm((current) => ({
      ...current,
      events: current.events.includes(eventName)
        ? current.events.filter((item) => item !== eventName)
        : [...current.events, eventName],
    }));
  };

  const copySecret = async () => {
    if (!createdSecret) return;
    try {
      await navigator.clipboard.writeText(createdSecret);
      toast.success("Chave copiada.");
    } catch {
      toast.error("Não foi possível copiar a chave. Selecione o texto e copie manualmente.");
    }
  };

  return (
    <section className="mb-8 mt-10" aria-labelledby="external-integrations-title">
      <div className="border-b border-slate-800">
        <div className="pb-5">
          <div className="flex items-center gap-2">
            <PlugZap className="h-4 w-4 text-cyan-400" />
            <h2 id="external-integrations-title" className="text-sm font-bold text-slate-200" style={{ fontFamily: "Outfit" }}>Integrações Externas e Automação</h2>
          </div>
          <p className="mt-2 max-w-3xl text-xs leading-relaxed text-slate-500">Configure chaves de API e webhooks para conectar o Cliver CRM a sistemas externos.</p>
        </div>
        <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Configurações de integrações externas">
          {visibleTabs.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setActiveTab(tab.id)} className={`relative shrink-0 px-4 py-3 text-xs font-medium transition-colors ${active ? "text-cyan-400" : "text-slate-500 hover:text-slate-300"}`}>
                {tab.label}
                {active && <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-cyan-400" />}
              </button>
            );
          })}
        </div>
      </div>

      {!canManage ? (
        <div className="mt-5 rounded-2xl border border-slate-800 bg-slate-900/60 p-8 text-center text-sm text-slate-500">Apenas administradores podem gerenciar integrações externas.</div>
      ) : (
        <div className="mt-5 space-y-4">
          {canUseApiKeys && visiblePanels.includes("api-keys") && (
            <ApiKeysPanel
              items={apiKeys}
              loading={loading}
              showRemoved={showRemoved}
              onToggleRemoved={() => setShowRemoved((value) => !value)}
              onCreate={() => setModal("api-create")}
              onDisable={disableKey}
              onDelete={deleteKey}
            />
          )}
          {canUseWebhooks && visiblePanels.includes("webhooks") && (
            <WebhooksPanel
              items={webhooks}
              loading={loading}
              onCreate={() => openWebhookModal()}
              onEdit={openWebhookModal}
              onTest={testWebhook}
              onLogs={openWebhookLogs}
              onDisable={disableWebhook}
              onDelete={deleteWebhook}
            />
          )}
          {showExternalEmptyState && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 text-center text-sm text-slate-500">
              Nenhum modulo externo individual foi liberado para este workspace.
            </div>
          )}
          {canUseDocs && visiblePanels.includes("documentation") && (
            <ApiDocumentationPanel />
          )}
        </div>
      )}

      {modal === "api-create" && (
        <ModalShell title="Gerar chave de API" onClose={closeModal}>
          <form onSubmit={createApiKey} className="space-y-4 p-6">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-300">Nome da chave</label>
              <Input required maxLength={120} value={apiForm.name} onChange={(event) => setApiForm((current) => ({ ...current, name: event.target.value }))} placeholder="Ex.: Integração n8n" className="border-slate-700 bg-slate-950 text-slate-100" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-300">Data de expiração (opcional)</label>
              <Input type="date" value={apiForm.expires_at} onChange={(event) => setApiForm((current) => ({ ...current, expires_at: event.target.value }))} className="border-slate-700 bg-slate-950 text-slate-100" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={closeModal} className="border-slate-700 bg-transparent text-slate-400">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-cyan-400 text-slate-950 hover:bg-cyan-300">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Gerar chave
              </Button>
            </div>
          </form>
        </ModalShell>
      )}

      {modal === "api-secret" && createdSecret && (
        <ModalShell title="Chave de API criada" onClose={closeModal}>
          <div className="p-6">
            <div className="flex items-start gap-3 rounded-xl border border-amber-400/20 bg-amber-400/10 p-4">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <p className="text-xs leading-relaxed text-amber-200">Copie agora. Esta chave não será exibida novamente.</p>
            </div>
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950 p-3">
              <code className="min-w-0 flex-1 break-all text-xs text-cyan-300">{createdSecret}</code>
              <button type="button" onClick={copySecret} aria-label="Copiar chave de API" className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-800 hover:text-slate-200" title="Copiar chave"><Copy className="h-4 w-4" /></button>
            </div>
            <Button type="button" onClick={closeModal} className="mt-5 w-full bg-slate-100 text-slate-950 hover:bg-white">Concluído</Button>
          </div>
        </ModalShell>
      )}

      {modal === "webhook-create" && (
        <ModalShell title={editingWebhook ? "Editar Webhook" : "Adicionar Webhook"} onClose={closeModal} maxWidth="max-w-xl">
          <form onSubmit={createWebhook} className="max-h-[75vh] space-y-4 overflow-y-auto p-6">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-300">Nome</label>
              <Input required maxLength={120} value={webhookForm.name} onChange={(event) => setWebhookForm((current) => ({ ...current, name: event.target.value }))} placeholder="Ex.: Webhook n8n" className="border-slate-700 bg-slate-950 text-slate-100" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-300">URL de destino</label>
              <Input required type="url" value={webhookForm.url} onChange={(event) => setWebhookForm((current) => ({ ...current, url: event.target.value }))} placeholder="https://seusistema.com/webhooks/cliver" className="border-slate-700 bg-slate-950 text-slate-100" />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium text-slate-300">Eventos</label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {events.map((eventName) => (
                  <label key={eventName} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2 text-xs text-slate-400">
                    <input type="checkbox" checked={webhookForm.events.includes(eventName)} onChange={() => toggleEvent(eventName)} className="h-4 w-4 accent-emerald-400" />
                    <code>{eventName}</code>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-300">Secret para assinatura (opcional)</label>
              <Input type="password" autoComplete="new-password" maxLength={500} value={webhookForm.secret} onChange={(event) => setWebhookForm((current) => ({ ...current, secret: event.target.value }))} placeholder="Usado para gerar X-Cliver-Signature" className="border-slate-700 bg-slate-950 text-slate-100" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={closeModal} className="border-slate-700 bg-transparent text-slate-400">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-400 text-slate-950 hover:bg-emerald-300">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editingWebhook ? "Salvar alterações" : "Adicionar"}
              </Button>
            </div>
          </form>
        </ModalShell>
      )}
      {modal === "webhook-logs" && (
        <WebhookLogsModal
          webhook={selectedWebhook}
          logs={webhookLogs}
          loading={logsLoading}
          canViewTechnical={canViewTechnical}
          selectedLog={selectedLog}
          onSelectLog={setSelectedLog}
          onClose={closeModal}
        />
      )}

    </section>
  );
}
